import {
  Alert,
  AwaitContainer,
  Card,
  CardContent,
  Menu,
  PlayPauseButton,
  RepeatButton,
  SeekVisualizer,
  Skeleton,
} from '@ibsys/kit';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useConversationTranscript, useDialogAudioState } from '@/entities/conversations';
import { useDialogSessionOptional } from '@/features/DialogSession';
import { cn } from '@/shared/lib';
import { InterruptionIntervals } from './InterruptionIntervals';
import { PauseIntervals } from './PauseIntervals';
import { SpeakerIntervals } from '@/pages/dialogs/DialogAudioSection/SpeakerIntervals';

export const DialogAudioSection: FC = () => {
  const { id } = useParams<{ id?: string }>();
  const { t } = useTranslation(['dialogs']);
  const dialogSession = useDialogSessionOptional();
  const transcriptQuery = useConversationTranscript(id || '');

  const transcriptData = dialogSession ? dialogSession.transcriptsQuery.data : (transcriptQuery.data ?? []);
  const isLoadingTranscript = dialogSession ? dialogSession.transcriptsQuery.isLoading : transcriptQuery.isLoading;
  const audioDuration = dialogSession?.duration ?? 0;

  const { status: audioStatus } = useDialogAudioState(id);

  // Записи нет вовсе — сразу «недоступна», без ожидания и индикатора.
  if (audioStatus === 'unavailable') {
    return (
      <div className={'p-1'}>
        <Card className={'w-full py-3'}>
          <CardContent className={'py-0'}>
            <Alert className="text-muted-foreground" title={t('audioUnavailable')} />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Состояние записи ещё неизвестно (карточка диалога грузится) — зарезервированное место без
  // индикатора, чтобы не было дёрганья индикатор → «недоступна» у диалогов без записи.
  if (audioStatus === 'loading') {
    return (
      <div className={'p-1 overflow-hidden'}>
        <Card className={'w-full py-3'}>
          <CardContent className={'py-0'}>
            <Skeleton className={'h-20 w-full'} />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Запись готова или ожидается — карточка плеера в зарезервированном месте; пока ссылка
  // не готова (pending), показываем индикатор, чтобы плеер появился без дёрганья разметки.
  return (
    <div className={'p-1 overflow-hidden'}>
      <Card className={'w-full py-3 '}>
        <AwaitContainer isLoading={audioStatus === 'pending' || isLoadingTranscript} className="rounded-xl">
          <CardContent className={'py-0'}>
            <div className={cn('flex items-center gap-2')} data-size={'md'}>
              <PlayPauseButton />
              <RepeatButton />
              <div className={'flex-1 relative w-full min-w-0'}>
                <SeekVisualizer
                  height={75}
                  className={'w-full'}
                  barColor={'var(--color-primary)'}
                  barPlayedColor={'var(--color-primary-hover)'}
                />
                <div className={'absolute inset-0 left-12 right-12 h-full pointer-events-none'}>
                  <SpeakerIntervals transcriptData={transcriptData} audioDuration={audioDuration} />
                  <PauseIntervals transcriptData={transcriptData} audioDuration={audioDuration} />
                  <InterruptionIntervals transcriptData={transcriptData} audioDuration={audioDuration} />
                </div>
              </div>
              <Menu />
            </div>
          </CardContent>
        </AwaitContainer>
      </Card>
    </div>
  );
};
